import { faCheck, faCopy, faEdit, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { tv } from "tailwind-variants";

import { cn } from "../../utils/cn";
import { Button } from "../button/Button";
import { Tooltip } from "../tooltip/Tooltip";
import { Text, textVariants } from "./Text";
import { useEditableParagraph } from "./useEditableParagraph";

import type { HTMLAttributes, ReactNode } from "react";
import type { VariantProps } from "tailwind-variants";
import type { ForwardRefComponentWithSubcomponents } from "../../utils/types";
import type { UseEditableParagraphProps } from "./useEditableParagraph";

const paragraphVariants = tv({
  extend: textVariants,
  base: "m-0 break-words",
  variants: {
    withLine: {
      true: [
        "flex",
        "before:content-['']",
        "after:content-['']",
        "before:flex-1",
        "after:flex-1",
        "before:border-b",
        "after:border-b",
        "before:border-solid",
        "after:border-solid",
        "before:border-b-gray-150",
        "after:border-b-gray-150",
        "before:border-t-0",
        "after:border-t-0",
        "before:border-l-0",
        "after:border-l-0",
        "before:border-r-0",
        "after:border-r-0",
        "before:m-auto",
        "after:m-auto",
        "before:mr-2",
        "after:ml-2",
        "dark:before:border-b-gray-700",
        "dark:after:border-b-gray-700",
      ],
    },
  },
});

/**
 * Configuration for editable behavior.
 * antd compatibility: supports { onChange: fn } pattern.
 */
export interface ParagraphEditableConfig {
  /** Callback fired when the text is changed */
  onChange?: (value: string) => void;
}

/**
 * Configuration for copyable behavior.
 * antd compatibility: supports { text, icon, tooltips, onCopy } pattern.
 */
export interface ParagraphCopyableConfig {
  /** Text to copy to clipboard. If not provided, uses children. */
  text?: string;
  /** Custom icon for the copy button */
  icon?: ReactNode;
  /** Tuple of tooltips: [beforeCopy, afterCopy] */
  tooltips?: [string, string];
  /** Callback fired after text is copied */
  onCopy?: () => void;
}

export interface ParagraphProps
  extends HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof paragraphVariants> {
  /**
   * Enable inline editing. Pass true for default behavior or a config object.
   * antd compatibility: when provided, renders an editable paragraph.
   */
  editable?: boolean | ParagraphEditableConfig;
  /**
   * Enable copy to clipboard. Pass true for default behavior or a config object.
   * antd compatibility: when provided, renders a copy button.
   */
  copyable?: boolean | ParagraphCopyableConfig;
}

/**
 * Internal component for copyable paragraph functionality
 */
const CopyableParagraphContent = ({
  children,
  copyableConfig,
  className,
  size,
  bold,
  emphasis,
  withLine,
  ...props
}: ParagraphProps & { copyableConfig: ParagraphCopyableConfig }) => {
  const [isCopied, setIsCopied] = useState(false);

  // Get text to copy - use config.text if provided, otherwise use children as string
  const textToClipboard = useMemo(() => {
    if (copyableConfig.text) return copyableConfig.text;
    if (typeof children === "string") return children;
    return "";
  }, [copyableConfig.text, children]);

  // Support tooltips array or defaults
  const beforeCopyTooltip = copyableConfig.tooltips?.[0] ?? "Copy";
  const afterCopyTooltip = copyableConfig.tooltips?.[1] ?? "Copied!";

  const handleCopyClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(textToClipboard);
      setIsCopied(true);
      copyableConfig.onCopy?.();
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  }, [textToClipboard, copyableConfig]);

  useEffect(() => {
    if (!isCopied) return;
    const timeout = setTimeout(() => setIsCopied(false), 1000);
    return () => clearTimeout(timeout);
  }, [isCopied]);

  // If no children, just show the copy button
  const showContent = children !== undefined && children !== "";

  return (
    <div className={cn("flex min-w-0 items-center gap-x-1", className)} {...props}>
      {showContent && (
        <p className={paragraphVariants({ size, bold, emphasis, withLine })}>{children}</p>
      )}
      <div className="shrink-0">
        {isCopied ? (
          <>
            <Text emphasis="secondary">{afterCopyTooltip}</Text>
            <FontAwesomeIcon className="pl-1 text-secondary" icon={faCheck} />
          </>
        ) : (
          <Tooltip content={beforeCopyTooltip}>
            <Button
              isIconOnly
              size="xs"
              variant="neutralLink"
              onClick={handleCopyClick}
              aria-label={`Copy ${textToClipboard}`}
            >
              {copyableConfig.icon ?? <FontAwesomeIcon icon={faCopy} />}
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export const Paragraph = forwardRef<HTMLParagraphElement, ParagraphProps>(
  ({ className, size, bold, emphasis, withLine, editable, copyable, children, ...props }, ref) => {
    // Handle editable prop - render Paragraph.Editable with mapped props
    if (editable) {
      const editableConfig =
        typeof editable === "object" ? editable : ({} as ParagraphEditableConfig);
      return (
        <EditableParagraph
          className={className}
          size={size}
          bold={bold}
          emphasis={emphasis}
          value={typeof children === "string" ? children : ""}
          onValueChange={editableConfig.onChange}
        />
      );
    }

    // Handle copyable prop - render with copy functionality
    if (copyable) {
      const copyableConfig =
        typeof copyable === "object" ? copyable : ({} as ParagraphCopyableConfig);
      return (
        <CopyableParagraphContent
          copyableConfig={copyableConfig}
          className={className}
          size={size}
          bold={bold}
          emphasis={emphasis}
          withLine={withLine}
          {...props}
        >
          {children}
        </CopyableParagraphContent>
      );
    }

    // Default rendering
    return (
      <p
        ref={ref}
        className={paragraphVariants({ className, size, bold, emphasis, withLine })}
        {...props}
      >
        {children}
      </p>
    );
  }
) as ForwardRefComponentWithSubcomponents<
  HTMLParagraphElement,
  {
    Editable: typeof EditableParagraph;
    Copyable: typeof CopyableParagraphContent;
  },
  ParagraphProps
>;
Paragraph.displayName = "Paragraph";

export type EditableParagraphProps = UseEditableParagraphProps & {
  size?: ParagraphProps["size"];
  bold?: ParagraphProps["bold"];
  emphasis?: ParagraphProps["emphasis"];
};

export const EditableParagraph = forwardRef<HTMLDivElement, EditableParagraphProps>(
  (props, ref) => {
    const {
      isEditing,
      textValue,
      baseProps,
      textareaProps,
      textareaRef,
      editButtonProps,
      confirmButtonProps,
      cancelButtonProps,
      paragraphProps,
      buttonGroupProps,
      viewContainerProps,
    } = useEditableParagraph(props);

    return (
      <div ref={ref} {...baseProps}>
        {isEditing ? (
          <>
            <textarea ref={textareaRef} {...textareaProps} />
            <div {...buttonGroupProps}>
              <Button {...confirmButtonProps}>
                <FontAwesomeIcon icon={faCheck} />
              </Button>
              <Button {...cancelButtonProps}>
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </div>
          </>
        ) : (
          <div {...viewContainerProps}>
            <Paragraph
              {...paragraphProps}
              size={props.size}
              bold={props.bold}
              emphasis={props.emphasis}
            >
              {textValue || props.placeholder || "Click to edit"}
            </Paragraph>
            <Button {...editButtonProps}>
              <FontAwesomeIcon icon={faEdit} />
            </Button>
          </div>
        )}
      </div>
    );
  }
);
EditableParagraph.displayName = "EditableParagraph";
Paragraph.Editable = EditableParagraph;

// Create a proper Copyable component for Paragraph.Copyable usage
const CopyableParagraph = forwardRef<
  HTMLDivElement,
  Omit<ParagraphProps, "editable" | "copyable"> & { copyable?: ParagraphCopyableConfig }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>(({ copyable = {}, ...props }, ref) => {
  // ref is not used directly but required for forwardRef signature
  return <CopyableParagraphContent copyableConfig={copyable} {...props} />;
});
CopyableParagraph.displayName = "Paragraph.Copyable";
Paragraph.Copyable = CopyableParagraph as typeof CopyableParagraphContent;
